/* eslint-disable react/prop-types */
import { SnackbarProvider } from 'notistack'
import React, { useEffect, useState } from 'react'
import { Switch, Route } from 'react-router-dom'
import useWidth from '@hooks/useWidth'
import application from '@config/application'
import ForgotPassword from '@containers/Pages/Auth/ForgotPassword'
import ForgotResult from '@containers/Pages/Auth/ForgotPassword/ForgotResult'
import Login from '@containers/Pages/Auth/Login'
import ResetPassword from '@containers/Pages/Auth/ResetPassword'
import NotFound from '@containers/Pages/NotFound'
import { createBrowserHistory } from 'history'
import Register from '@containers/Pages/Auth/Register'
import AffiliateRegister from '@containers/Pages/Auth/Register/AffiliateRegistration'
import RegisterPayment from '@containers/Pages/Auth/Register/RegisterPayment'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import DayjsUtils from '@date-io/dayjs'
import ManualPayment from '@containers/Pages/Payment'
import Affiliate from '@containers/Pages/Auth/Affiliate'
import { storeFrontApi } from '@api/baseApi'
import getDomain from '@config/domain'
import LoadingPage from '@components/LoadingPage'
import { Box, Typography } from '@material-ui/core'
import { useFeature } from '@hooks/useFeature'
import AdminRoutes from './AdminRoutes'
import AffiliateRoutes from './AffiliateRoutes'

const history = createBrowserHistory({
  basename: '/reseller/',
})

const Routes = () => {
  const width = useWidth()
  const [snackbarProps, setSnackbarProps] = useState({
    maxSnack: 3,
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'center',
    },
  })

  useEffect(() => {
    if (width === 'sm' || width === 'xs') {
      setSnackbarProps({
        ...snackbarProps,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      })
    } else {
      setSnackbarProps({
        ...snackbarProps,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
      })
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width])

  const [isDomainAvailable, setIsDomainAvailable] = useState(false)
  const [isDomainActive, setIsDomainActive] = useState(false)
  const [suspendedAccount, setSuspendedAccount] = useState(null)
  const [loading, setLoading] = useState(false)
  const [tawkToLink, setTawkToLink] = useState()
  const { getFeatures } = useFeature()

  const checkDomain = async () => {
    setLoading(true)
    try {
      const { status, data: { data } } = await storeFrontApi.get('/api/v1/stores/show_by_domain', { params: { domain: getDomain() } })
      const packageFeature = data?.package_features?.[0] ?? {}
      const dummyFeatures = {
        isPartialPayment: packageFeature?.can_access_partial_payment ?? false,
        isMemberships: packageFeature?.can_access_memberships ?? false,
        isPreOrder: packageFeature?.can_access_pre_order ?? false,
        isKeepStocks: packageFeature?.can_access_keep_stocks ?? false,
        isMemberReports: packageFeature?.can_access_member_reports ?? false,
        isSourcePayments: packageFeature?.can_access_money_sources ?? false,
        isInternalDropship: packageFeature?.can_access_internal_dropships ?? false,
        isRetailPrice: packageFeature?.can_access_price_variation ?? false,
        isMemberVisit: packageFeature?.can_access_daily_visit ?? false,
      }
      if (status === 200) {
        getFeatures(dummyFeatures)
        setLoading(false)
        setIsDomainAvailable(true)
        setIsDomainActive(data?.status)
        setSuspendedAccount(data?.suspended_account)
        if (data?.tawk_to_link) setTawkToLink(data?.tawk_to_link?.replace('https://tawk.to/chat/', 'https://embed.tawk.to/'))
        await localStorage.setItem('store_name', data?.name)
      } else {
        setLoading(false)
        setIsDomainAvailable(false)
      }
    } catch (error) {
      setLoading(false)
      setIsDomainAvailable(false)
    }
  }

  useEffect(() => {
    checkDomain()
  }, [])

  useEffect(() => {
    if (tawkToLink && tawkToLink.startsWith('https://embed.tawk.to/')) {
      const script = document.createElement('script')
      script.src = tawkToLink
      script.async = true

      document.body.appendChild(script)
    }
  }, [tawkToLink])

  const app = () => (
    <SnackbarProvider {...snackbarProps}>
      <MuiPickersUtilsProvider utils={DayjsUtils}>
        <Switch history={history}>
          <Route exact path={`${application.prefix}/`} component={Login} />
          <Route path={`${application.prefix}/login`} component={Login} />
          <Route exact path={`${application.prefix}/affiliate`} component={Affiliate} />
          <Route exact path={`${application.prefix}/forgot-password`} component={ForgotPassword} />
          <Route path={`${application.prefix}/forgot-password/result`} component={ForgotResult} />
          <Route path={`${application.prefix}/reset_password`} component={ResetPassword} />
          <Route exact path={`${application.prefix}/register`} component={Register} />
          <Route exact path={`${application.prefix}/register/affiliate`} component={AffiliateRegister} />
          <Route path={`${application.prefix}/register-payment`} component={RegisterPayment} />
          <Route path={`${application.prefix}/payments`} component={ManualPayment} />
          <Route
            path={application.prefix + application.path}
            render={(props) => <AdminRoutes {...props} />}
          />
          <Route
            path={`${application.prefix}/affiliate`}
            render={(props) => <AffiliateRoutes {...props} />}
          />
          <Route component={NotFound} />
        </Switch>
      </MuiPickersUtilsProvider>
    </SnackbarProvider>
  )

  const renderApp = () => (
    isDomainActive && suspendedAccount !== 'locked'
      ? app()
      : (
        <Box
          display="flex"
          position="fixed"
          height="100%"
          width="100%"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <Typography variant="h3" color="secondary" gutterBottom>
            {
              suspendedAccount === 'locked'
                ? 'Maaf, akun anda telah di suspend.'
                : `Maaf, ${window.location.hostname} belum aktif.`
              }
          </Typography>
          <Typography variant="inherit">
            {
              suspendedAccount === 'locked'
                ? <div> Silahkan periksa status akun anda dan ikuti petunjuk pada dashboard pengguna. <a href="https://client.squids.id">Atur Toko</a></div>
                : <div> Punya Toko ini? <a href="https://client.squids.id">Atur Toko</a></div>
              }

          </Typography>
        </Box>
      ))

  const renderActiveApp = () => (isDomainAvailable ? renderApp() : <NotFound />)

  return loading ? <LoadingPage /> : renderActiveApp()
}
export default Routes
